import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {IUserStore} from "data/stores/user/user.store";
import {action, makeAutoObservable, observable} from "mobx";
import {Bindings} from "data/constants/bindings";
import {RequestState} from "data/enums";
import {isEqual} from "lodash";
import type {ChangeEvent, FocusEvent, SyntheticEvent} from "react";
import type {AxiosError} from "axios";
import type {IApiResponse} from "data/services/http";
import {extractErrorMessage} from "data/utils";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {PASSWORD_REQUIREMENTS, POSTCODE_REQUIREMENTS} from "data/constants";
import {SelectChangeEvent} from "@mui/material";
import type {ICountriesStore, ICountry} from "data/stores/countries/countries.store";
import type {IRegistrationPayload} from "data/providers/api/user.api.provider";

interface IRegistrationFormElement extends HTMLFormElement {
	username: HTMLInputElement;
	firstName: HTMLInputElement;
	lastName: HTMLInputElement;
	email: HTMLInputElement;
	country: HTMLInputElement;
	postcode: HTMLInputElement;
	password: HTMLInputElement;
	confirmPassword: HTMLInputElement;
	terms: HTMLInputElement;
	optInEu: HTMLInputElement;
	optInUs: HTMLInputElement;
}

export interface IRegistrationController extends ViewController {
	get i18n(): ILocalizationStore;
	get isFormDisabled(): boolean;
	get error(): Record<string, string> | null;
	get userData(): Record<string, string>;
	get countriesList(): ICountry[];
	get isSubmitDisabled(): boolean;
	get isCountryUS(): boolean;

	handleFormSubmit: (event: SyntheticEvent<IRegistrationFormElement>) => void;
	handleFormOnChange: (event: ChangeEvent<IRegistrationFormElement>) => void;
	handleInputSetValue: (event: ChangeEvent<HTMLInputElement>) => void;
	handleValidatePassword: (event: FocusEvent<HTMLInputElement>) => void;
	handleValidatePostcode: (event: FocusEvent<HTMLInputElement>) => void;
	handleSelectChange: (event: SelectChangeEvent<string>) => void;
}

@injectable()
export class RegistrationController implements IRegistrationController {
	@observable private _requestState = RequestState.IDLE;
	@observable private _errorMsg: string | null = null;
	@observable private _errorPlace: string = "";
	@observable private _displayName: string = "";
	@observable private _firstName: string = "";
	@observable private _lastName: string = "";
	@observable private _country: string = "";
	@observable private _postcode: string = "";
	@observable private _isSubmitDisabled: boolean = true;

	get i18n() {
		return this._i18nStore;
	}

	get error() {
		if (!this._errorMsg) return null;

		return {
			[this._errorPlace || "common"]: this._errorMsg,
		};
	}

	get userData() {
		return {
			displayName: this._displayName,
			firstName: this._firstName,
			lastName: this._lastName,
			country: this._country,
			postcode: this._postcode,
		};
	}

	get isFormDisabled(): boolean {
		return isEqual(this._requestState, RequestState.PENDING);
	}

	get isSubmitDisabled(): boolean {
		return this._isSubmitDisabled;
	}

	get countriesList(): ICountry[] {
		return this._countries.list;
	}

	get isCountryUS(): boolean {
		return isEqual(this._country, "USA");
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.CountriesStore) private _countries: ICountriesStore
	) {
		makeAutoObservable(this);
	}

	@action private reportError(error: string, place: string = "") {
		this._errorMsg = error;
		this._errorPlace = place;

		return true;
	}

	@action register(payload: IRegistrationPayload) {
		this._requestState = RequestState.PENDING;
		return this._userStore.register(payload).catch(this.onError);
	}

	@action public handleFormSubmit = (event: SyntheticEvent<IRegistrationFormElement>) => {
		event.preventDefault();

		const {
			username,
			firstName,
			lastName,
			country,
			postcode,
			email,
			password,
			confirmPassword,
			terms,
			optInEu,
			optInUs,
		} = event.currentTarget;

		const validateList = [
			{field: email, error: "Please provide a valid email address", place: "email"},
			{field: username, error: "Please provide your display name", place: "username"},
			{field: firstName, error: "Please provide your first name", place: "firstName"},
			{field: lastName, error: "Please provide your last name", place: "lastName"},
			{field: country, error: "Please provide your country", place: "country"},
			{field: password, error: PASSWORD_REQUIREMENTS, place: "password"},
			{field: terms, error: "Please accept Terms & Conditions", place: "terms"},
			...(this.isCountryUS
				? [{field: postcode, error: POSTCODE_REQUIREMENTS, place: "postcode"}]
				: []),
		];

		const hasError = validateList.find(({field, error, place}) =>
			field.checkValidity() ? false : this.reportError(error, place)
		);

		if (hasError) {
			return;
		}

		// if (email.value !== confirmEmail.value) {
		// 	return this.reportError("Emails do not match", "confirmEmail");
		// }

		if (password.value !== confirmPassword.value) {
			return this.reportError("Passwords do not match", "confirmPassword");
		}

		const payload = {
			username: username.value,
			email: email.value,
			password: password.value,
			firstName: firstName.value,
			lastName: lastName.value,
			country: country.value,
			terms: terms.checked,
			optInEu: optInEu.checked,
			optInUs: optInUs.checked,
			...(this.isCountryUS && {
				postcode: postcode.value,
			}),
			// lang: this._i18nStore.lang,
		};

		this._requestState = RequestState.PENDING;
		void this.register(payload).catch(this.onError);
	};

	@action private onError = (error: AxiosError<IApiResponse>) => {
		this._errorMsg = extractErrorMessage(error);
		this._requestState = RequestState.ERROR;
	};

	@action handleFormOnChange = (event: ChangeEvent<IRegistrationFormElement>) => {
		const {
			username,
			firstName,
			lastName,
			country,
			email,
			password,
			confirmPassword,
			terms,
			postcode,
		} = event.currentTarget;

		const dataArr = [
			username.value,
			firstName.value,
			lastName.value,
			country.value,
			email.value,
			password.value,
			confirmPassword.value,
			terms.checked,
			...(this.isCountryUS ? [postcode.value] : []),
			// dayjs(dob.value as Dayjs | null).isValid(),
		];

		if (event.target.name !== "password") {
			this._errorMsg = null;
			this._errorPlace = "";
		}

		if (dataArr.every(Boolean)) {
			this._isSubmitDisabled = false;

			return;
		}

		this._isSubmitDisabled = true;
	};

	@action handleInputSetValue = (event: ChangeEvent<HTMLInputElement>) => {
		const {value, name} = event.target;
		if (name === "firstName") {
			this._firstName = value;
			return;
		}
		if (name === "lastName") {
			this._lastName = value;
			return;
		}
		if (name === "postcode") {
			this._postcode = value;
			return;
		}
		this._displayName = value.replace("@", "");
	};

	@action handleValidatePassword = (event: FocusEvent<HTMLInputElement>) => {
		if (event.target.checkValidity()) {
			this._errorMsg = null;
		} else {
			this.reportError(PASSWORD_REQUIREMENTS, "password");
		}
	};

	@action handleValidatePostcode = (event: FocusEvent<HTMLInputElement>) => {
		if (event.target.checkValidity()) {
			this._errorMsg = null;
		} else {
			this.reportError(POSTCODE_REQUIREMENTS, "postcode");
		}
	};

	@action handleSelectChange = (event: SelectChangeEvent<string>) => {
		const {value, name} = event.target;
		if (name === "country") {
			this._country = value;
		}
	};

	dispose(): void {
		return;
	}

	init() {
		void this._countries.fetchCountries();
	}
}
